// Questions & Answers Subpage - Shows all Q&A content with search and filtering
// Follows the Section, Component & Manager System architecture

import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MainLayout } from '../../../../components/Layout';
import { LearnManager } from '../../managers/learn-manager';
import { useManager } from '../../../../core';
import { LearnHeader, LearnPageHeader, SearchBar, LearnLoading, QAItem } from '../../components';
import { useAuth } from '../../../../contexts/AuthContext';
import '../../LearnShared.css';
import '../../LearnSection.css';

export function QuestionsAnswersSection() {
  const { manager, isInitialized } = useManager(LearnManager);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredQA, setFilteredQA] = useState([]);

  const qaParam = searchParams.get('qa')?.trim() ?? '';

  useEffect(() => {
    if (!isInitialized || !manager) return;

    setSearchTerm(manager.searchTerm || '');

    if (qaParam) {
      const one = manager.getQuestionAnswerById(qaParam);
      setFilteredQA(one ? [one] : []);
    } else {
      setFilteredQA(manager.getFilteredQuestionsAnswers());
    }
  }, [isInitialized, manager, manager?.searchTerm, qaParam]);

  useLayoutEffect(() => {
    if (!qaParam || filteredQA.length !== 1) return;
    const el = document.getElementById(`learn-qa-card-${qaParam}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const heading = el.querySelector('.learn-qa-question');
    if (heading && typeof heading.focus === 'function') {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  }, [qaParam, filteredQA]);

  const handleSearchChange = (term) => {
    manager.setSearchTerm(term);
    setSearchTerm(term);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('qa');
      return next;
    }, { replace: true });
  };

  const clearSingleQuestionView = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('qa');
      return next;
    }, { replace: true });
  };

  const handleLikeToggle = async (qaId) => {
    if (!user) return;

    try {
      const result = await manager.toggleLikeQA(qaId);

      setFilteredQA((prevQA) =>
        prevQA.map((qa) =>
          qa.id === qaId
            ? { ...qa, helpful: result.likes, userLiked: result.liked }
            : qa
        )
      );
    } catch (error) {
      console.error('Failed to toggle like:', error);
    }
  };

  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--learn">
        <LearnLoading
          title="Loading Questions & Answers"
          subtitle="Gathering knowledge base..."
        />
      </MainLayout>
    );
  }

  const isMissingDeepLink = Boolean(qaParam) && filteredQA.length === 0;
  const isEmptyList = !qaParam && filteredQA.length === 0;

  return (
    <MainLayout contentClassName="main-content--learn">
      <LearnPageHeader />

      <div className="learn-container">
        <LearnHeader />

        {qaParam && filteredQA.length === 1 && (
          <div className="learn-qa-single-banner">
            <span>Viewing one question.</span>
            <button type="button" className="learn-qa-single-banner__btn" onClick={clearSingleQuestionView}>
              Show all questions
            </button>
          </div>
        )}

        <SearchBar
          mode="qaListFilter"
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          placeholder="Search by question title or #tag..."
        />

        <div className="learn-qa-section">
          {isMissingDeepLink ? (
            <div className="learn-empty-state">
              <i className="fa-solid fa-circle-exclamation"></i>
              <h3>Question not found</h3>
              <p>This link may be outdated or the question may have been removed.</p>
              <button type="button" className="learn-qa-single-banner__btn learn-qa-single-banner__btn--inline" onClick={clearSingleQuestionView}>
                Browse all questions
              </button>
            </div>
          ) : isEmptyList ? (
            <div className="learn-empty-state">
              <i className="fa-solid fa-question-circle"></i>
              <h3>No questions found</h3>
              <p>Try adjusting your search terms or check back later for new content.</p>
            </div>
          ) : (
            <div className="learn-qa-list">
              {filteredQA.map((qa) => (
                <QAItem
                  key={qa.id}
                  qa={qa}
                  isAdmin={false}
                  onLikeToggle={handleLikeToggle}
                  user={user}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
